import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { useSetAtom } from 'jotai';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '../../../../hooks/useWidgetPopout';
import { MetricLabel } from '../../common/MetricLabel';
import { getMetricDescriptionWithFormula } from '~/utils/metricsMapping';

interface MarketMetric {
  label: string;
  value: string;
  trend?: 'up' | 'down' | 'neutral';
}

interface SectorMomentumBreakdown {
  sector_name: string;
  metric: {
    trend: 'up' | 'down' | 'neutral';
    value: string;
    confidence: number;
  };
}

export type MarketAnalysisWidgetProps = {
  marketMetrics: MarketMetric[];
  sectorMomentumBreakdown: SectorMomentumBreakdown[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  timestamp?: string;
  snapshotId?: string | null;
};

export const MarketAnalysisWidget: React.FC<MarketAnalysisWidgetProps> = ({
  marketMetrics,
  sectorMomentumBreakdown,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Market Analysis',
  timestamp,
  snapshotId,
}) => {
  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: MarketAnalysisWidget,
    title: title || 'Market Analysis Widget',
    defaultWidth: 1000,
    defaultHeight: 700,
    widgetProps: {
      marketMetrics,
      sectorMomentumBreakdown,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      timestamp,
      snapshotId,
    },
  });

  const noData = useMemo(() => {
    return !marketMetrics.length;
  }, [marketMetrics]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No Market Data Available."
          subTitle="Run portfolio analysis to generate market analysis"
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
        children={
          timestamp && (
            <span className="ml-2 bq-timestamp-badge">{timestamp}</span>
          )
        }
      />

      {/* Full Height Content with Scrollable Cards */}
      <div className="flex-1 px-3 pt-3 pb-2 overflow-y-auto">
        {/* Market Metrics */}
        <div className="grid" style={{ minHeight: '100%' }}>
          {/* Left column: Sector Momentum (full height) */}
          <div className="col-12 md:col-6 pb-0">
            {(() => {
              const sectorMomentum = marketMetrics.find(
                (m) => m.label === 'Sector Momentum',
              );
              return sectorMomentum ? (
                <Card
                  className="flex flex-column"
                  style={{ height: '100%' }}
                  pt={{
                    body: { className: 'p-3 flex-1' },
                    content: { className: 'p-0 m-0 flex-1' },
                  }}
                >
                  <MetricLabel
                    label={sectorMomentum.label}
                    description={getMetricDescriptionWithFormula(
                      sectorMomentum.label,
                    )}
                  />
                  <div
                    className="text-lg font-semibold mb-3"
                    style={{
                      color:
                        sectorMomentum.value?.includes('-') ||
                        sectorMomentum.value?.startsWith('-')
                          ? 'var(--bq-color-error)'
                          : sectorMomentum.value?.startsWith('+')
                            ? 'var(--bq-color-sucess)'
                            : 'var(--bq-text-primary)',
                    }}
                  >
                    {sectorMomentum.trend === 'up' && '↗ '}
                    {sectorMomentum.trend === 'down' && '↘ '}
                    {sectorMomentum.value}
                  </div>

                  {/* Show sector breakdown inside Sector Momentum card */}
                  {sectorMomentumBreakdown &&
                    sectorMomentumBreakdown.length > 0 && (
                      <div
                        className="mt-3 pt-3 border-top-1 flex-1"
                        style={{ borderColor: 'var(--bq-border-muted)' }}
                      >
                        <div
                          className="text-xs mb-2"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          Sector Breakdown:
                        </div>
                        <div className="grid">
                          {sectorMomentumBreakdown.map(
                            (sector, sectorIndex) => (
                              <div
                                key={`sector-${sectorIndex}`}
                                className="col-12"
                              >
                                <div className="flex justify-content-between align-items-center py-1">
                                  <span
                                    className="text-xs"
                                    style={{ color: 'var(--bq-text-primary)' }}
                                  >
                                    {sector.sector_name}
                                  </span>
                                  <span
                                    className="text-xs font-semibold"
                                    style={{
                                      color:
                                        sector.metric.value?.includes('-') ||
                                        sector.metric.value?.startsWith('-')
                                          ? 'var(--bq-color-error)'
                                          : sector.metric.value?.startsWith('+')
                                            ? 'var(--bq-color-sucess)'
                                            : 'var(--bq-text-primary)',
                                    }}
                                  >
                                    {sector.metric.trend === 'up' && '↗ '}
                                    {sector.metric.trend === 'down' && '↘ '}
                                    {sector.metric.value}
                                  </span>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </Card>
              ) : null;
            })()}
          </div>

          {/* Right column: Avg P/E Ratio, News Sentiment, and Volatility Index stacked */}
          <div className="col-12 md:col-6 pb-0">
            <div className="flex flex-column gap-3" style={{ height: '100%' }}>
              {/* Avg P/E Ratio */}
              {(() => {
                const avgPeRatio = marketMetrics.find(
                  (m) => m.label === 'Avg P/E Ratio',
                );
                return avgPeRatio ? (
                  <Card
                    className="flex-1 flex flex-column"
                    pt={{
                      body: { className: 'p-3 flex-1' },
                      content: { className: 'p-0 m-0 flex-1' },
                    }}
                  >
                    <MetricLabel
                      label={avgPeRatio.label}
                      description={getMetricDescriptionWithFormula(
                        avgPeRatio.label,
                      )}
                    />
                    <div
                      className="text-lg font-semibold"
                      style={{
                        color:
                          avgPeRatio.value?.includes('-') ||
                          avgPeRatio.value?.startsWith('-')
                            ? 'var(--bq-color-error)'
                            : avgPeRatio.value?.startsWith('+')
                              ? 'var(--bq-color-sucess)'
                              : 'var(--bq-text-primary)',
                      }}
                    >
                      {avgPeRatio.trend === 'up' && '↗ '}
                      {avgPeRatio.trend === 'down' && '↘ '}
                      {avgPeRatio.value}
                    </div>
                  </Card>
                ) : null;
              })()}

              {/* News Sentiment */}
              {(() => {
                const newsSentiment = marketMetrics.find(
                  (m) => m.label === 'News Sentiment',
                );
                return newsSentiment ? (
                  <Card
                    className="flex-1 flex flex-column"
                    pt={{
                      body: { className: 'p-3 flex-1' },
                      content: { className: 'p-0 m-0 flex-1' },
                    }}
                  >
                    <MetricLabel
                      label={newsSentiment.label}
                      description={getMetricDescriptionWithFormula(
                        newsSentiment.label,
                      )}
                    />
                    <div
                      className="text-lg font-semibold"
                      style={{
                        color:
                          newsSentiment.value?.includes('-') ||
                          newsSentiment.value?.startsWith('-')
                            ? 'var(--bq-color-error)'
                            : newsSentiment.value?.startsWith('+')
                              ? 'var(--bq-color-sucess)'
                              : 'var(--bq-text-primary)',
                      }}
                    >
                      {newsSentiment.trend === 'up' && '↗ '}
                      {newsSentiment.trend === 'down' && '↘ '}
                      {newsSentiment.value}
                    </div>
                  </Card>
                ) : null;
              })()}

              {/* Volatility Index */}
              {(() => {
                const volatilityIndex = marketMetrics.find(
                  (m) => m.label === 'Volatility Index',
                );
                return volatilityIndex ? (
                  <Card
                    className="flex-1 flex flex-column"
                    pt={{
                      body: { className: 'p-3 flex-1' },
                      content: { className: 'p-0 m-0 flex-1' },
                    }}
                  >
                    <MetricLabel
                      label={volatilityIndex.label}
                      description={getMetricDescriptionWithFormula(
                        volatilityIndex.label,
                      )}
                    />
                    <div
                      className="text-lg font-semibold"
                      style={{
                        color:
                          volatilityIndex.value?.includes('-') ||
                          volatilityIndex.value?.startsWith('-')
                            ? 'var(--bq-color-error)'
                            : volatilityIndex.value?.startsWith('+')
                              ? 'var(--bq-color-sucess)'
                              : 'var(--bq-text-primary)',
                      }}
                    >
                      {volatilityIndex.trend === 'up' && '↗ '}
                      {volatilityIndex.trend === 'down' && '↘ '}
                      {volatilityIndex.value}
                    </div>
                  </Card>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
